import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import "./FindVerse.css";
import {
  getEnglishBookName,
  getTamilBookName,
  getDeutschBookName,
} from "../BookNames";
import { AuthContext } from "../../Authentication/AuthContext";
import {
  getBookChapterCount,
  getChapterVerses,
  getStaticBibleSections,
  prefetchWholeBookInBackground,
  startBibleDownloadCache,
} from "../DownloadCache";

const TESTAMENT_SELECT_VALUE = "";
const NEIGHBOR_PREFETCH_DELAY_MS = 120;

const normalizeLanguageKey = (appLanguage) => {
  if (appLanguage === "தமிழ்") return "tamil";
  if (appLanguage === "Deutsch") return "deutsch";
  return "english";
};

const normalizeIncomingLanguageKey = (languageValue) => {
  const normalizedValue = String(languageValue || "")
    .trim()
    .toLowerCase();

  if (!normalizedValue) return "";

  if (normalizedValue === "tamil" || normalizedValue === "தமிழ்") {
    return "tamil";
  }

  if (normalizedValue === "english") {
    return "english";
  }

  if (normalizedValue === "deutsch" || normalizedValue === "german") {
    return "deutsch";
  }

  return "";
};

const getSelectedLanguagesFromHome = ({
  statePrefill,
  params,
  fallbackLanguage,
}) => {
  const stateLanguages = Array.isArray(statePrefill.selectedLanguages)
    ? statePrefill.selectedLanguages
    : [];

  const queryLanguages = String(params.get("languages") || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

  const incomingLanguages =
    stateLanguages.length > 0 ? stateLanguages : queryLanguages;

  const selectedHomeLanguages = incomingLanguages.reduce(
    (uniqueLanguages, item) => {
      const normalizedLanguage = normalizeIncomingLanguageKey(item);

      if (normalizedLanguage && !uniqueLanguages.includes(normalizedLanguage)) {
        uniqueLanguages.push(normalizedLanguage);
      }

      return uniqueLanguages;
    },
    [],
  );

  if (selectedHomeLanguages.length > 0) {
    return selectedHomeLanguages;
  }

  return [normalizeLanguageKey(fallbackLanguage)];
};

const getBookCacheKey = (bookValue) =>
  String(bookValue || "")
    .trim()
    .toLowerCase();

const getCompactBookKey = (bookValue) =>
  String(bookValue || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const getChapterCacheKey = (testamentType, bookKey, chapterNumber) => {
  return `${testamentType}:${getBookCacheKey(bookKey)}:${Number(chapterNumber)}`;
};

const resolveBookKeyFromBooks = (books, bookValue, fallbackValue = "") => {
  if (!bookValue) return fallbackValue;

  const incomingCompactKey = getCompactBookKey(bookValue);
  const matchedBook = books.find(
    (book) => getCompactBookKey(book.book_name) === incomingCompactKey,
  );

  if (matchedBook) return getBookCacheKey(matchedBook.book_name);
  return getBookCacheKey(bookValue);
};

const getOriginalBookNameFromBooks = (books, bookValue) => {
  if (!bookValue) return "";

  const incomingCompactKey = getCompactBookKey(bookValue);
  const matchedBook = books.find(
    (book) => getCompactBookKey(book.book_name) === incomingCompactKey,
  );

  return matchedBook?.book_name || bookValue;
};

const getLanguageText = (lang) => {
  if (lang === "tamil") return "Tamil";
  if (lang === "deutsch") return "Deutsch";
  return "English";
};

const getBookDisplayName = (bookName, appLanguage) => {
  if (appLanguage === "English") return getEnglishBookName(bookName);
  if (appLanguage === "Deutsch") return getDeutschBookName(bookName);
  return getTamilBookName(bookName);
};

const getTestamentDisplayName = (testamentName, appLanguage) => {
  if (appLanguage === "English") return testamentName;

  if (appLanguage === "Deutsch") {
    return testamentName === "Old Testament"
      ? "Altes Testament"
      : "Neues Testament";
  }

  return testamentName === "Old Testament" ? "பழைய ஏற்பாடு" : "புதிய ஏற்பாடு";
};

const getLabels = (appLanguage) => {
  if (appLanguage === "English") {
    return {
      testament: "Testament",
      book: "Book",
      chapter: "Chapter",
      verse: "Verse",
      language: "Language",
      select: "Select",
      versePlaceholder: "Example: 1 or 1-14",
      note: "Note: select the testament, select the book, select the chapter, Example: 1 or 1-14.",
      submit: "Submit",
      reset: "Reset",
      noResults: "No verses found.",
      selectTestamentAlert: "Please select the testament.",
      selectBookAlert: "Please select the book.",
      selectChapterAlert: "Please select the chapter.",
      enterVerseAlert: "Please enter the verse number.",
      selectLanguageAlert: "Please select at least one language.",
      invalidVerseAlert: "Please enter a valid verse number or range.",
      alertTitle: "Required",
      previous: "‹ Previous",
      next: "Next ›",
    };
  }

  if (appLanguage === "Deutsch") {
    return {
      testament: "Testament",
      book: "Buch",
      chapter: "Kapitel",
      verse: "Vers",
      language: "Sprache",
      select: "Select",
      versePlaceholder: "Example: 1 or 1-14",
      note: "Note: Testament auswählen, Buch auswählen, Kapitel auswählen, Beispiel: 1 oder 1-14.",
      submit: "Submit",
      reset: "Reset",
      noResults: "Keine Verse gefunden.",
      selectTestamentAlert: "Bitte Testament auswählen.",
      selectBookAlert: "Bitte Buch auswählen.",
      selectChapterAlert: "Bitte Kapitel auswählen.",
      enterVerseAlert: "Bitte Versnummer eingeben.",
      selectLanguageAlert: "Bitte mindestens eine Sprache auswählen.",
      invalidVerseAlert:
        "Bitte eine gültige Versnummer oder einen gültigen Bereich eingeben.",
      alertTitle: "Required",
      previous: "‹ Vorheriges",
      next: "Nächstes ›",
    };
  }

  return {
    testament: "ஏற்பாடு",
    book: "ஆகமம்",
    chapter: "அதிகாரம்",
    verse: "வசனம்",
    language: "மொழி",
    select: "Select",
    versePlaceholder: "Example: 1 or 1-14",
    note: "Note: ஏற்பாட்டைத் தேர்ந்தெடுக்கவும், ஆகமத்தைத் தேர்ந்தெடுக்கவும், அதிகாரத்தைத் தேர்ந்தெடுக்கவும், Example: 1 or 1-14.",
    submit: "Submit",
    reset: "Reset",
    noResults: "வசனங்கள் கிடைக்கவில்லை.",
    selectTestamentAlert: "ஏற்பாட்டைத் தேர்ந்தெடுக்கவும்.",
    selectBookAlert: "ஆகமத்தைத் தேர்ந்தெடுக்கவும்.",
    selectChapterAlert: "அதிகாரத்தைத் தேர்ந்தெடுக்கவும்.",
    enterVerseAlert: "வசன எண்ணை உள்ளிடவும்.",
    selectLanguageAlert: "குறைந்தது ஒரு மொழியைத் தேர்ந்தெடுக்கவும்.",
    invalidVerseAlert: "சரியான வசன எண் அல்லது வரம்பை உள்ளிடவும்.",
    alertTitle: "தேவை",
    previous: "‹ முந்தையது",
    next: "அடுத்தது ›",
  };
};

const getTestamentTypeFromName = (testamentName) =>
  testamentName === "Old Testament" ? "old" : "new";

const getTestamentSectionFromType = (testaments, testamentType) => {
  if (!testamentType) return null;

  return testaments.find(
    (testament) =>
      getTestamentTypeFromName(testament.testament) === testamentType,
  );
};

const parseVerseInput = (value) => {
  const cleanedValue = String(value || "")
    .trim()
    .replace(/\s+/g, "")
    .replace(/[–—]/g, "-");

  if (!cleanedValue) return null;

  if (cleanedValue.includes("-")) {
    const [startRaw, endRaw] = cleanedValue.split("-");
    const start = Number.parseInt(startRaw, 10);
    const end = Number.parseInt(endRaw, 10);

    if (
      !Number.isFinite(start) ||
      !Number.isFinite(end) ||
      start <= 0 ||
      end <= 0
    ) {
      return null;
    }

    if (start > end) return null;

    return {
      start,
      end,
    };
  }

  const verseNumberValue = Number.parseInt(cleanedValue, 10);

  if (!Number.isFinite(verseNumberValue) || verseNumberValue <= 0) return null;

  return {
    start: verseNumberValue,
    end: verseNumberValue,
  };
};

const CustomSelect = ({
  id,
  value,
  options,
  placeholder,
  onChange,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);

  const selectedOption = options.find((option) => option.value === value);
  const displayText = selectedOption ? selectedOption.label : placeholder;

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!selectRef.current || selectRef.current.contains(event.target)) {
        return;
      }

      setIsOpen(false);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, []);

  const handleOptionClick = (nextValue) => {
    onChange(nextValue);
    setIsOpen(false);
  };

  return (
    <div className="bibleversefind-custom-select" ref={selectRef}>
      <button
        id={id}
        type="button"
        disabled={disabled}
        className={`bibleversefind-custom-select-button ${
          value ? "has-selected-value" : ""
        }`}
        onClick={() => setIsOpen((previousValue) => !previousValue)}
      >
        <span className="bibleversefind-custom-select-text">{displayText}</span>

        {!value ? (
          <span
            className="bibleversefind-custom-select-arrow"
            aria-hidden="true"
          >
            <svg
              viewBox="0 0 24 24"
              className="bibleversefind-custom-select-arrow-icon"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </span>
        ) : null}
      </button>

      {isOpen && !disabled && (
        <div className="bibleversefind-custom-select-options">
          {options.map((option) => (
            <button
              key={`${id}-${option.value}`}
              type="button"
              className={`bibleversefind-custom-select-option ${
                option.value === value ? "active" : ""
              }`}
              onClick={() => handleOptionClick(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

function FindVerse() {
  const { language } = useContext(AuthContext);
  const location = useLocation();

  const isMountedRef = useRef(true);
  const activeRequestRef = useRef(0);
  const resultContainerRef = useRef(null);
  const homePrefillAppliedRef = useRef(false);
  const chapterCacheRef = useRef(new Map());

  const [testaments, setTestaments] = useState([]);
  const [selectedTestament, setSelectedTestament] = useState("");
  const [selectedBook, setSelectedBook] = useState("");
  const [selectedChapter, setSelectedChapter] = useState("");
  const [verseNumber, setVerseNumber] = useState("");
  const [verses, setVerses] = useState([]);
  const [error, setError] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [shouldAutoSubmit, setShouldAutoSubmit] = useState(false);
  const [selectedLanguages, setSelectedLanguages] = useState([
    normalizeLanguageKey(language),
  ]);

  const labels = useMemo(() => getLabels(language), [language]);
  const availableLanguages = useMemo(() => ["tamil", "english", "deutsch"], []);

  const homePrefill = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const statePrefill = location.state?.prefill || {};
    const hasHomePrefill =
      params.get("home") === "1" || location.state?.fromHome === true;

    if (!hasHomePrefill) return null;

    return {
      testamentType:
        statePrefill.testamentType || params.get("testament") || "",
      book: statePrefill.book || params.get("book") || "",
      chapter: statePrefill.chapter || params.get("chapter") || "",
      verse: statePrefill.verse || params.get("verse") || "",
      selectedLanguages: getSelectedLanguagesFromHome({
        statePrefill,
        params,
        fallbackLanguage: language,
      }),
    };
  }, [language, location.search, location.state]);

  useEffect(() => {
    isMountedRef.current = true;

    startBibleDownloadCache();
    setTestaments(getStaticBibleSections());

    return () => {
      isMountedRef.current = false;
      activeRequestRef.current += 1;
    };
  }, []);

  const selectedTestamentObject = useMemo(() => {
    return testaments.find(
      (testament) => String(testament.id) === String(selectedTestament),
    );
  }, [selectedTestament, testaments]);

  const selectedTestamentType = useMemo(() => {
    if (!selectedTestamentObject) return null;
    return getTestamentTypeFromName(selectedTestamentObject.testament);
  }, [selectedTestamentObject]);

  const books = useMemo(() => {
    return selectedTestamentObject?.books || [];
  }, [selectedTestamentObject]);

  const selectedOriginalBookName = useMemo(() => {
    return getOriginalBookNameFromBooks(books, selectedBook);
  }, [books, selectedBook]);

  const chapters = useMemo(() => {
    if (!selectedBook || !selectedTestamentType) return [];

    const chapterCount = getBookChapterCount(
      selectedTestamentType,
      selectedBook,
    );

    if (chapterCount <= 0) return [];

    return Array.from({ length: chapterCount }, (_, index) => ({
      id: index + 1,
      chapter_number: index + 1,
    }));
  }, [selectedBook, selectedTestamentType]);

  const selectedChapterCount = chapters.length;

  const chapterHeaderText = useMemo(() => {
    if (
      !selectedOriginalBookName ||
      !selectedChapter ||
      selectedLanguages.length === 0
    ) {
      return "";
    }

    const bookNamesByLanguage = {
      tamil: getTamilBookName(selectedOriginalBookName),
      english: getEnglishBookName(selectedOriginalBookName),
      deutsch: getDeutschBookName(selectedOriginalBookName),
    };

    const bookNames = selectedLanguages
      .map((lang) => bookNamesByLanguage[lang])
      .filter(Boolean)
      .join(" | ");

    return `${bookNames} : ${selectedChapter}`;
  }, [selectedChapter, selectedLanguages, selectedOriginalBookName]);

  const getChapterPromise = useCallback(
    ({ testamentType, bookKey, chapterNumber }) => {
      if (!testamentType || !bookKey || !chapterNumber) {
        return Promise.resolve([]);
      }

      const safeBookKey = getBookCacheKey(bookKey);
      const safeChapterNumber = Number(chapterNumber);
      const cacheKey = getChapterCacheKey(
        testamentType,
        safeBookKey,
        safeChapterNumber,
      );

      const cachedEntry = chapterCacheRef.current.get(cacheKey);

      if (cachedEntry?.verses && cachedEntry.verses.length > 0) {
        return Promise.resolve(cachedEntry.verses);
      }

      if (cachedEntry?.promise) {
        return cachedEntry.promise;
      }

      const requestPromise = getChapterVerses(
        testamentType,
        safeBookKey,
        safeChapterNumber,
      )
        .then((loadedVerses) => {
          const safeVerses = Array.isArray(loadedVerses) ? loadedVerses : [];

          chapterCacheRef.current.set(cacheKey, {
            verses: safeVerses,
            promise: null,
          });

          return safeVerses;
        })
        .catch(() => {
          chapterCacheRef.current.delete(cacheKey);
          return [];
        });

      chapterCacheRef.current.set(cacheKey, {
        verses: null,
        promise: requestPromise,
      });

      return requestPromise;
    },
    [],
  );

  const warmChapter = useCallback(
    ({ testamentType, bookKey, chapterNumber }) => {
      if (!testamentType || !bookKey || !chapterNumber) return;

      getChapterPromise({
        testamentType,
        bookKey,
        chapterNumber,
      });
    },
    [getChapterPromise],
  );

  const warmNeighborChapters = useCallback(
    ({ testamentType, bookKey, chapterNumber }) => {
      if (!testamentType || !bookKey || !chapterNumber) return;

      const chapterCount = getBookChapterCount(testamentType, bookKey);
      const currentChapterNumber = Number(chapterNumber);

      if (!chapterCount || !Number.isFinite(currentChapterNumber)) return;

      window.setTimeout(() => {
        if (currentChapterNumber > 1) {
          warmChapter({
            testamentType,
            bookKey,
            chapterNumber: currentChapterNumber - 1,
          });
        }

        if (currentChapterNumber < chapterCount) {
          warmChapter({
            testamentType,
            bookKey,
            chapterNumber: currentChapterNumber + 1,
          });
        }
      }, NEIGHBOR_PREFETCH_DELAY_MS);
    },
    [warmChapter],
  );

  const getAdjacentChapterTarget = useCallback(
    (direction) => {
      if (!selectedBook || !selectedChapter || !selectedTestamentType) {
        return null;
      }

      const currentChapterNumber = Number(selectedChapter);

      if (!Number.isFinite(currentChapterNumber)) return null;

      if (direction === "previous") {
        if (currentChapterNumber <= 1) {
          return null;
        }

        return {
          testamentId: selectedTestament,
          testamentType: selectedTestamentType,
          bookKey: selectedBook,
          originalBookName: selectedOriginalBookName,
          chapterNumber: currentChapterNumber - 1,
        };
      }

      if (currentChapterNumber >= selectedChapterCount) {
        return null;
      }

      return {
        testamentId: selectedTestament,
        testamentType: selectedTestamentType,
        bookKey: selectedBook,
        originalBookName: selectedOriginalBookName,
        chapterNumber: currentChapterNumber + 1,
      };
    },
    [
      selectedBook,
      selectedChapter,
      selectedChapterCount,
      selectedOriginalBookName,
      selectedTestament,
      selectedTestamentType,
    ],
  );

  const previousChapterTarget = useMemo(
    () => getAdjacentChapterTarget("previous"),
    [getAdjacentChapterTarget],
  );

  const nextChapterTarget = useMemo(
    () => getAdjacentChapterTarget("next"),
    [getAdjacentChapterTarget],
  );

  const filterVersesByInput = useCallback((chapterVerses, verseInput) => {
    const safeVerses = Array.isArray(chapterVerses) ? chapterVerses : [];
    const parsedVerseInput = parseVerseInput(verseInput);

    if (!parsedVerseInput) return safeVerses;

    return safeVerses.filter((verse) => {
      const verseNo = Number(verse.verse_number);

      return (
        verseNo >= parsedVerseInput.start && verseNo <= parsedVerseInput.end
      );
    });
  }, []);

  const loadChapter = useCallback(
    async ({
      testamentId,
      testamentType,
      bookKey,
      originalBookName,
      chapterNumber,
      verseInput = "",
      shouldScroll = true,
    }) => {
      const requestId = activeRequestRef.current + 1;
      activeRequestRef.current = requestId;

      const safeBookKey = getBookCacheKey(bookKey);
      const safeChapterNumber = Number(chapterNumber);

      setError("");
      setVerses([]);
      setIsSubmitted(true);

      try {
        const loadedVerses = await getChapterPromise({
          testamentType,
          bookKey: safeBookKey,
          chapterNumber: safeChapterNumber,
        });

        if (!isMountedRef.current || activeRequestRef.current !== requestId) {
          return;
        }

        const filteredVerses = filterVersesByInput(loadedVerses, verseInput);

        setSelectedTestament(String(testamentId));
        setSelectedBook(safeBookKey);
        setSelectedChapter(String(safeChapterNumber));
        setVerseNumber(verseInput || "");
        setVerses(
          filteredVerses.map((verse) => ({
            ...verse,
            book_name: verse.book_name || originalBookName,
          })),
        );

        if (filteredVerses.length === 0) {
          setError(labels.noResults);
        } else {
          setError("");
        }

        warmNeighborChapters({
          testamentType,
          bookKey: safeBookKey,
          chapterNumber: safeChapterNumber,
        });

        if (shouldScroll) {
          window.setTimeout(() => {
            resultContainerRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }, 20);
        }
      } catch (requestError) {
        if (!isMountedRef.current || activeRequestRef.current !== requestId) {
          return;
        }

        setVerses([]);
        setError(labels.noResults);
      }
    },
    [
      filterVersesByInput,
      getChapterPromise,
      labels.noResults,
      warmNeighborChapters,
    ],
  );

  useEffect(() => {
    if (!testaments.length || !homePrefill || homePrefillAppliedRef.current) {
      return;
    }

    const testamentSection = getTestamentSectionFromType(
      testaments,
      homePrefill.testamentType,
    );

    if (!testamentSection) return;

    const testamentType = getTestamentTypeFromName(testamentSection.testament);

    const resolvedBook = resolveBookKeyFromBooks(
      testamentSection.books,
      homePrefill.book,
      "",
    );

    const originalBookName =
      getOriginalBookNameFromBooks(testamentSection.books, resolvedBook) ||
      resolvedBook;

    setSelectedTestament(String(testamentSection.id));
    setSelectedBook(resolvedBook || "");
    setSelectedChapter(homePrefill.chapter || "");
    setVerseNumber(homePrefill.verse || "");
    setSelectedLanguages(
      homePrefill.selectedLanguages || [normalizeLanguageKey(language)],
    );
    setVerses([]);
    setError("");
    setIsSubmitted(false);

    homePrefillAppliedRef.current = true;

    if (homePrefill.testamentType && resolvedBook && homePrefill.chapter) {
      warmChapter({
        testamentType,
        bookKey: resolvedBook,
        chapterNumber: Number(homePrefill.chapter),
      });
    }

    if (
      homePrefill.testamentType &&
      resolvedBook &&
      homePrefill.chapter &&
      homePrefill.verse
    ) {
      setShouldAutoSubmit(true);
    }

    if (
      homePrefill.testamentType &&
      resolvedBook &&
      homePrefill.chapter &&
      !homePrefill.verse
    ) {
      loadChapter({
        testamentId: String(testamentSection.id),
        testamentType,
        bookKey: resolvedBook,
        originalBookName,
        chapterNumber: Number(homePrefill.chapter),
        verseInput: "",
        shouldScroll: false,
      });
    }
  }, [homePrefill, language, loadChapter, testaments, warmChapter]);

  const resetFormFields = useCallback(() => {
    activeRequestRef.current += 1;

    const defaultLanguage = normalizeLanguageKey(language);

    setSelectedTestament("");
    setSelectedBook("");
    setSelectedChapter("");
    setVerseNumber("");
    setVerses([]);
    setError("");
    setAlertMessage("");
    setIsSubmitted(false);
    setShouldAutoSubmit(false);
    setSelectedLanguages([defaultLanguage]);
  }, [language]);

  useEffect(() => {
    if (homePrefill) return;
    resetFormFields();
  }, [language, homePrefill, resetFormFields]);

  useEffect(() => {
    if (!selectedBook || !selectedTestamentType) return;

    prefetchWholeBookInBackground(selectedTestamentType, selectedBook);
  }, [selectedBook, selectedTestamentType]);

  useEffect(() => {
    if (!selectedBook || !selectedTestamentType || !selectedChapter) return;

    warmChapter({
      testamentType: selectedTestamentType,
      bookKey: selectedBook,
      chapterNumber: Number(selectedChapter),
    });

    warmNeighborChapters({
      testamentType: selectedTestamentType,
      bookKey: selectedBook,
      chapterNumber: Number(selectedChapter),
    });
  }, [
    selectedBook,
    selectedChapter,
    selectedTestamentType,
    warmChapter,
    warmNeighborChapters,
  ]);

  const testamentOptions = useMemo(() => {
    return [
      {
        value: TESTAMENT_SELECT_VALUE,
        label: labels.select,
      },
      ...testaments.map((testament) => ({
        value: String(testament.id),
        label: getTestamentDisplayName(testament.testament, language),
      })),
    ];
  }, [labels.select, language, testaments]);

  const bookOptions = useMemo(() => {
    return [
      {
        value: "",
        label: labels.select,
      },
      ...books.map((book) => {
        const bookKey = getBookCacheKey(book.book_name);

        return {
          value: bookKey,
          label: getBookDisplayName(book.book_name, language),
        };
      }),
    ];
  }, [books, labels.select, language]);

  const chapterOptions = useMemo(() => {
    return [
      {
        value: "",
        label: labels.select,
      },
      ...chapters.map((chapter) => ({
        value: String(chapter.chapter_number),
        label: String(chapter.chapter_number),
      })),
    ];
  }, [chapters, labels.select]);

  const orderedLanguages = useMemo(() => {
    const selectedSet = new Set(selectedLanguages);

    return [
      ...selectedLanguages,
      ...availableLanguages.filter((lang) => !selectedSet.has(lang)),
    ];
  }, [availableLanguages, selectedLanguages]);

  const handleTestamentChange = (value) => {
    activeRequestRef.current += 1;

    setSelectedTestament(value);
    setSelectedBook("");
    setSelectedChapter("");
    setVerseNumber("");
    setVerses([]);
    setError("");
    setIsSubmitted(false);
  };

  const handleBookChange = (value) => {
    activeRequestRef.current += 1;

    const resolvedBook = resolveBookKeyFromBooks(books, value, "");

    setSelectedBook(resolvedBook || "");
    setSelectedChapter("");
    setVerseNumber("");
    setVerses([]);
    setError("");
    setIsSubmitted(false);

    if (resolvedBook && selectedTestamentType) {
      prefetchWholeBookInBackground(selectedTestamentType, resolvedBook);
    }
  };

  const handleChapterChange = (value) => {
    activeRequestRef.current += 1;

    setSelectedChapter(value);
    setVerseNumber("");
    setVerses([]);
    setError("");
    setIsSubmitted(false);

    if (value && selectedBook && selectedTestamentType) {
      warmChapter({
        testamentType: selectedTestamentType,
        bookKey: selectedBook,
        chapterNumber: Number(value),
      });
    }
  };

  const toggleLanguage = (lang) => {
    setSelectedLanguages((previousValue) => {
      const isSelected = previousValue.includes(lang);

      if (isSelected && previousValue.length === 1) {
        return previousValue;
      }

      if (isSelected) {
        return previousValue.filter((item) => item !== lang);
      }

      return [...previousValue, lang];
    });
  };

  const showAlert = (message) => {
    setAlertMessage(message);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setAlertMessage("");
    setError("");
    setVerses([]);
    setIsSubmitted(true);

    if (!selectedTestament) {
      showAlert(labels.selectTestamentAlert);
      return;
    }

    if (!selectedBook) {
      showAlert(labels.selectBookAlert);
      return;
    }

    if (!selectedChapter) {
      showAlert(labels.selectChapterAlert);
      return;
    }

    if (!verseNumber.trim()) {
      showAlert(labels.enterVerseAlert);
      return;
    }

    const parsedVerseInput = parseVerseInput(verseNumber);

    if (!parsedVerseInput) {
      showAlert(labels.invalidVerseAlert);
      return;
    }

    if (!selectedLanguages.length) {
      showAlert(labels.selectLanguageAlert);
      return;
    }

    await loadChapter({
      testamentId: selectedTestament,
      testamentType: selectedTestamentType,
      bookKey: selectedBook,
      originalBookName: selectedOriginalBookName || selectedBook,
      chapterNumber: Number(selectedChapter),
      verseInput: verseNumber,
      shouldScroll: true,
    });
  };

  useEffect(() => {
    if (!shouldAutoSubmit) return;

    if (
      !selectedTestament ||
      !selectedBook ||
      !selectedChapter ||
      !verseNumber
    ) {
      return;
    }

    const parsedVerseInput = parseVerseInput(verseNumber);

    if (!parsedVerseInput) return;

    setShouldAutoSubmit(false);

    loadChapter({
      testamentId: selectedTestament,
      testamentType: selectedTestamentType,
      bookKey: selectedBook,
      originalBookName: selectedOriginalBookName || selectedBook,
      chapterNumber: Number(selectedChapter),
      verseInput: verseNumber,
      shouldScroll: false,
    });
  }, [
    loadChapter,
    selectedBook,
    selectedChapter,
    selectedOriginalBookName,
    selectedTestament,
    selectedTestamentType,
    shouldAutoSubmit,
    verseNumber,
  ]);

  const handleReset = () => {
    resetFormFields();
    homePrefillAppliedRef.current = false;
  };

  const handleAdjacentChapterClick = (target) => {
    if (!target) return;

    loadChapter({
      testamentId: target.testamentId,
      testamentType: target.testamentType,
      bookKey: target.bookKey,
      originalBookName: target.originalBookName,
      chapterNumber: target.chapterNumber,
      verseInput: "",
      shouldScroll: true,
    });
  };

  const getVerseNumberText = (verse) => {
    return `${verse.verse_number}. `;
  };

  const renderVerse = (verse, index) => {
    const verseNumberText = getVerseNumberText(verse);

    return (
      <div
        key={`${verse.book_name}-${verse.chapter_number}-${verse.verse_number}-${index}`}
        className="verse-row"
      >
        {selectedLanguages.map((lang, langIndex) => (
          <p
            key={`${lang}-${verse.verse_number}`}
            className={`verse-line bibleversefind-line-grid ${
              langIndex > 0 ? "bibleversefind-secondary-line" : ""
            }`}
          >
            {langIndex === 0 ? (
              <span className="verse-number-inline">{verseNumberText}</span>
            ) : (
              <span className="verse-number-spacer">{verseNumberText}</span>
            )}

            <span className="bibleversefind-verse-text">
              {verse[lang] || ""}
            </span>
          </p>
        ))}
      </div>
    );
  };

  return (
    <div className="bibleversefind-container">
      {alertMessage && (
        <div className="bibleversefind-alert-overlay">
          <div className="bibleversefind-alert-box">
            <div className="bibleversefind-alert-title">
              {labels.alertTitle}
            </div>
            <div className="bibleversefind-alert-message">{alertMessage}</div>

            <div className="bibleversefind-alert-actions">
              <button
                type="button"
                className="bibleversefind-alert-button"
                onClick={() => setAlertMessage("")}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bibleversefind-form-container">
        <form onSubmit={handleSubmit}>
          <div className="bibleversefind-form-row">
            <label htmlFor="bibleversefind-testament">{labels.testament}</label>

            <CustomSelect
              id="bibleversefind-testament"
              value={selectedTestament}
              options={testamentOptions}
              placeholder={labels.select}
              onChange={handleTestamentChange}
            />
          </div>

          <div className="bibleversefind-form-row">
            <label htmlFor="bibleversefind-book">{labels.book}</label>

            <CustomSelect
              id="bibleversefind-book"
              value={selectedBook}
              options={bookOptions}
              placeholder={labels.select}
              onChange={handleBookChange}
              disabled={!selectedTestament}
            />
          </div>

          <div className="bibleversefind-form-row">
            <label htmlFor="bibleversefind-chapter">{labels.chapter}</label>

            <CustomSelect
              id="bibleversefind-chapter"
              value={selectedChapter}
              options={chapterOptions}
              placeholder={labels.select}
              onChange={handleChapterChange}
              disabled={!selectedBook}
            />
          </div>

          <div className="bibleversefind-form-row">
            <label htmlFor="bibleversefind-verse">{labels.verse}</label>

            <input
              type="text"
              id="bibleversefind-verse"
              dir="ltr"
              value={verseNumber}
              onChange={(event) => setVerseNumber(event.target.value)}
              placeholder={labels.versePlaceholder}
            />
          </div>

          <div className="bibleversefind-form-row bibleversefind-language-row">
            <label>{labels.language}</label>

            <div className="bibleversefind-language-checkboxes">
              {orderedLanguages.map((lang) => (
                <label key={lang}>
                  <input
                    type="checkbox"
                    value={lang}
                    checked={selectedLanguages.includes(lang)}
                    onChange={() => toggleLanguage(lang)}
                  />
                  {getLanguageText(lang)}
                </label>
              ))}
            </div>
          </div>

          <div className="bibleversefind-submit-reset-buttons">
            <button type="submit" className="bibleversefind-submit-button">
              {labels.submit}
            </button>

            <button
              type="button"
              onClick={handleReset}
              className="bibleversefind-reset-button"
            >
              {labels.reset}
            </button>
          </div>
        </form>
      </div>

      <div className="bibleversefind-result-container" ref={resultContainerRef}>
        {verses.length > 0 ? (
          <div>
            {chapterHeaderText ? (
              <div className="bibleversefind-chapter-header">
                <div className="bibleversefind-chapter-header-title">
                  {chapterHeaderText}
                </div>

                <div className="bibleversefind-chapter-nav">
                  <button
                    type="button"
                    className="bibleversefind-chapter-nav-button"
                    disabled={!previousChapterTarget}
                    onClick={() =>
                      handleAdjacentChapterClick(previousChapterTarget)
                    }
                  >
                    {labels.previous}
                  </button>

                  <button
                    type="button"
                    className="bibleversefind-chapter-nav-button"
                    disabled={!nextChapterTarget}
                    onClick={() =>
                      handleAdjacentChapterClick(nextChapterTarget)
                    }
                  >
                    {labels.next}
                  </button>
                </div>
              </div>
            ) : null}

            <div className="bibleversefind-results-list">
              {verses.map((verse, index) => renderVerse(verse, index))}
            </div>
          </div>
        ) : (
          <p>{isSubmitted && error ? error : labels.note}</p>
        )}
      </div>
    </div>
  );
}

export default FindVerse;
